import { Navigate, Route, Routes } from "react-router-dom";
import Authenticated from "./middleware/Authenticated";
import RoleGuard from "./middleware/RoleGuard";
import PermGuard from "./middleware/PermGuard";
import AddonGuard from "./middleware/AddonGuard";
import AppLayout from "./layouts/AppLayout";
import AuthLayout from "./layouts/AuthLayout";

import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";

import AdminDashboardPage from "./pages/admin/DashboardPage";
import AdminClientsPage from "./pages/admin/ClientsPage";
import AdminBusinessDetailPage from "./pages/admin/BusinessDetailPage";
import AdminTaskOversightPage from "./pages/admin/TaskOversightPage";
import AdminTaskMastersPage from "./pages/admin/TaskMastersPage";
import AdminTaskGroupsPage from "./pages/admin/TaskGroupsPage";
import AdminDepartmentsPage from "./pages/admin/DepartmentsPage";
import AdminEmployeesPage from "./pages/admin/EmployeesPage";
import AdminAdminsPage from "./pages/admin/AdminsPage";
import AdminOpsPage from "./pages/admin/OpsPage";
import AdminServerPage from "./pages/admin/ServerPage";
import AdminGeofencesPage from "./pages/admin/GeofencesPage";
import AdminFirmProfilesPage from "./pages/admin/FirmProfilesPage";
import AdminInvoicesPage from "./pages/admin/InvoicesPage";
import AdminInvoiceFormPage from "./pages/admin/InvoiceFormPage";
import AdminInvoiceViewPage from "./pages/admin/InvoiceViewPage";
import OrgDrivePage from "./pages/admin/OrgDrivePage";
import OrgFieldsPage from "./pages/admin/OrgFieldsPage";
import PaymentMastersPage from "./pages/admin/PaymentMastersPage";
import PaymentsOversightPage from "./pages/admin/PaymentsOversightPage";
import ClientPrefixesPage from "./pages/admin/ClientPrefixesPage";
import AdminTicketsPage from "./pages/admin/TicketsPage";
import AdminTicketDetailPage from "./pages/admin/TicketDetailPage";
import AdminCalendarPage from "./pages/admin/CalendarPage";
import AdminNotificationsPage from "./pages/admin/NotificationsPage";
import AdminTodosPage from "./pages/admin/TodosPage";

import AdminAttendanceRegisterPage from "./pages/admin/AttendanceRegisterPage";
import AdminAttendanceReportsPage from "./pages/admin/AttendanceReportsPage";
import AdminAttendanceSettingsPage from "./pages/admin/AttendanceSettingsPage";
import AdminLeavesPage from "./pages/admin/AdminLeavesPage";
import MyAttendancePage from "./pages/employee/MyAttendancePage";
import MyLeavesPage from "./pages/employee/MyLeavesPage";

import MyTasksPage from "./pages/employee/MyTasksPage";
import MyHistoryPage from "./pages/employee/MyHistoryPage";
import MyCalendarPage from "./pages/employee/MyCalendarPage";
import MyTodosPage from "./pages/employee/MyTodosPage";
import MyAssignedTicketsPage from "./pages/employee/MyAssignedTicketsPage";
import MyAssignedTicketDetailPage from "./pages/employee/MyAssignedTicketDetailPage";
import MyTaskDetailPage from "./pages/employee/MyTaskDetailPage";

import CustomerOverviewPage from "./pages/customer/OverviewPage";
import CustomerTicketsPage from "./pages/customer/TicketsPage";
import CustomerTicketDetailPage from "./pages/customer/TicketDetailPage";
import CustomerNewTicketPage from "./pages/customer/NewTicketPage";
import CustomerFilesPage from "./pages/customer/FilesPage";

import SuperOverviewPage from "./pages/super/OverviewPage";
import SuperFirmsListPage from "./pages/super/FirmsListPage";
import SuperFirmDetailPage from "./pages/super/FirmDetailPage";
import SuperAddonsPage from "./pages/super/AddonsPage";
import SuperServerPage from "./pages/super/ServerPage";
import SuperSessionsPage from "./pages/super/SessionsPage";
import SuperActiveUsersPage from "./pages/super/ActiveUsersPage";
import SuperAnnouncementsPage from "./pages/super/AnnouncementsPage";
import SuperLeadsPage from "./pages/super/LeadsPage";
import AdminChangelogPage from "./pages/admin/ChangelogPage";
import AdminSubscriptionPage from "./pages/admin/SubscriptionPage";
import ChatPage from "./pages/shared/chat/ChatPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import AccountPage from "./pages/AccountPage";

export default function AppNavigator() {
  return (
    <Routes>
      {/* Public marketing landing page. Redirects logged-in users to their
          dashboard from inside the component (see LandingPage). */}
      <Route path="/" element={<LandingPage />} />

      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      <Route element={<Authenticated />}>
        {/* Forced first-login password change — no app chrome so it can't be
            navigated around (the Authenticated guard redirects here). */}
        <Route path="/change-password" element={<ChangePasswordPage />} />

        <Route element={<AppLayout />}>
          {/* Self-service account + password change, available to every role. */}
          <Route path="/account" element={<AccountPage />} />

          {/* Platform owner */}
          <Route element={<RoleGuard allow="super_admin" />}>
            <Route path="/super" element={<SuperOverviewPage />} />
            <Route path="/super/firms" element={<SuperFirmsListPage />} />
            <Route path="/super/firms/:id" element={<SuperFirmDetailPage />} />
            <Route path="/super/addons" element={<SuperAddonsPage />} />
            <Route path="/super/announcements" element={<SuperAnnouncementsPage />} />
            <Route path="/super/leads" element={<SuperLeadsPage />} />
            <Route path="/super/active-users" element={<SuperActiveUsersPage />} />
            <Route path="/super/sessions" element={<SuperSessionsPage />} />
            <Route path="/super/server" element={<SuperServerPage />} />
          </Route>

          {/* Admin */}
          <Route element={<RoleGuard allow="admin" />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/clients" element={<AdminClientsPage />} />
            <Route path="/admin/client-prefixes" element={<ClientPrefixesPage />} />
            {/* Legacy /admin/clients/:id redirects to business detail since
                clients and businesses are now the same entity. */}
            <Route path="/admin/clients/:id" element={<AdminBusinessDetailPage />} />
            <Route path="/admin/businesses/:id" element={<AdminBusinessDetailPage />} />
            <Route path="/admin/oversight" element={<AdminTaskOversightPage />} />
            <Route path="/admin/task-masters" element={<AdminTaskMastersPage />} />
            <Route path="/admin/task-groups" element={<AdminTaskGroupsPage />} />
            <Route path="/admin/departments" element={<AdminDepartmentsPage />} />
            <Route path="/admin/employees" element={<AdminEmployeesPage />} />
            <Route path="/admin/admins" element={<AdminAdminsPage />} />
            <Route path="/admin/ops" element={<AdminOpsPage />} />
            <Route path="/admin/server" element={<AdminServerPage />} />
            <Route path="/admin/firm-profiles" element={<AdminFirmProfilesPage />} />
            <Route path="/admin/changelog" element={<AdminChangelogPage />} />
            <Route path="/admin/invoices" element={<AdminInvoicesPage />} />
            <Route path="/admin/invoices/new" element={<AdminInvoiceFormPage />} />
            <Route path="/admin/invoices/:id/edit" element={<AdminInvoiceFormPage />} />
            <Route path="/admin/invoices/:id" element={<AdminInvoiceViewPage />} />
            <Route path="/admin/org-kb/drive" element={<OrgDrivePage />} />
            <Route path="/admin/org-kb/fields" element={<OrgFieldsPage />} />
            <Route path="/admin/payments" element={<PaymentsOversightPage />} />
            <Route path="/admin/payments/masters" element={<PaymentMastersPage />} />
            <Route path="/admin/tickets" element={<AdminTicketsPage />} />
            <Route path="/admin/tickets/:id" element={<AdminTicketDetailPage />} />
            {/* Drive used to live at /admin/drive; it's a tab on the business
                detail page now. Redirect any saved bookmarks. */}
            <Route path="/admin/drive" element={<Navigate to="/admin/clients" replace />} />
            <Route path="/admin/calendar" element={<AdminCalendarPage />} />
            <Route path="/admin/todos" element={<AdminTodosPage />} />
            <Route path="/admin/notifications" element={<AdminNotificationsPage />} />
            <Route path="/admin/subscription" element={<AdminSubscriptionPage />} />
            <Route element={<AddonGuard any={["chat", "chat_extended"]} />}>
              <Route path="/admin/chat" element={<ChatPage />} />
            </Route>
            <Route element={<AddonGuard any={["geofence"]} />}>
              <Route path="/admin/geofences" element={<AdminGeofencesPage />} />
            </Route>
            <Route element={<AddonGuard any={["attendance"]} />}>
              <Route path="/admin/attendance" element={<AdminAttendanceRegisterPage />} />
              <Route path="/admin/attendance/reports" element={<AdminAttendanceReportsPage />} />
              <Route path="/admin/attendance/settings" element={<AdminAttendanceSettingsPage />} />
              <Route path="/admin/leaves" element={<AdminLeavesPage />} />
              {/* Admins are tracked too — same self-service pages, admin tree. */}
              <Route path="/admin/my-attendance" element={<MyAttendancePage />} />
              <Route path="/admin/my-leaves" element={<MyLeavesPage />} />
            </Route>
          </Route>

          {/* Employee */}
          <Route element={<RoleGuard allow="employee" />}>
            <Route path="/me" element={<MyTasksPage />} />
            <Route path="/me/tasks/:id" element={<MyTaskDetailPage />} />
            <Route path="/me/history" element={<MyHistoryPage />} />
            <Route path="/me/calendar" element={<MyCalendarPage />} />
            <Route path="/me/todos" element={<MyTodosPage />} />
            <Route path="/me/tickets" element={<MyAssignedTicketsPage />} />
            <Route path="/me/tickets/:id" element={<MyAssignedTicketDetailPage />} />
            <Route element={<AddonGuard any={["chat", "chat_extended"]} />}>
              <Route path="/me/chat" element={<ChatPage />} />
            </Route>
            <Route element={<AddonGuard any={["attendance"]} />}>
              <Route path="/me/attendance" element={<MyAttendancePage />} />
              <Route path="/me/leaves" element={<MyLeavesPage />} />
            </Route>

            {/* Employees with at least one client.* perm get to reuse the
                admin Clients UI; per-action buttons (create/edit/delete)
                gate themselves on perms inside the pages. */}
            <Route element={<PermGuard any={["client.view", "client.add", "client.edit"]} />}>
              <Route path="/me/clients" element={<AdminClientsPage />} />
              <Route path="/me/clients/:id" element={<AdminBusinessDetailPage />} />
              <Route path="/me/businesses/:id" element={<AdminBusinessDetailPage />} />
            </Route>

            {/* Org knowledge base is a shared resource — employees with
                org-kb.view get the same UI as admins; edit/delete buttons
                self-gate on the corresponding perms. */}
            <Route element={<PermGuard any={["org-kb.view", "org-kb.edit", "org-kb.delete"]} />}>
              <Route path="/me/org-kb/drive" element={<OrgDrivePage />} />
              <Route path="/me/org-kb/fields" element={<OrgFieldsPage />} />
            </Route>

            {/* Payments oversight — employees with payments.view get the same
                page as admins; edit actions self-gate on payments.edit.
                Same pattern for invoices: list + view need payments.view,
                generate/edit/delete affordances self-gate on payments.edit. */}
            <Route element={<PermGuard any={["payments.view", "payments.edit"]} />}>
              <Route path="/me/payments" element={<PaymentsOversightPage />} />
              <Route path="/me/invoices" element={<AdminInvoicesPage />} />
              <Route path="/me/invoices/new" element={<AdminInvoiceFormPage />} />
              <Route path="/me/invoices/:id/edit" element={<AdminInvoiceFormPage />} />
              <Route path="/me/invoices/:id" element={<AdminInvoiceViewPage />} />
            </Route>
          </Route>

          {/* Customer */}
          <Route element={<RoleGuard allow="customer" />}>
            <Route path="/portal" element={<CustomerOverviewPage />} />
            <Route path="/portal/tickets" element={<CustomerTicketsPage />} />
            <Route path="/portal/tickets/new" element={<CustomerNewTicketPage />} />
            <Route path="/portal/tickets/:id" element={<CustomerTicketDetailPage />} />
            <Route path="/portal/files" element={<CustomerFilesPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
